import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Form,
  Row,
  Col,
  Badge,
  Alert,
  Tabs,
  Tab,
  ProgressBar,
  Table,
  Modal,
  InputGroup,
  Spinner
} from 'react-bootstrap';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import BacktestResults from './BacktestResults.js';

const DEFAULT_SYMBOLS = [
  'SPY', 'QQQ', 'IWM', 'GLD', 'TLT',
  'XLF', 'XLE', 'XLK', 'XLV', 'XLP', 'XLU',
  'SMH', 'RSP'
];

const AVAILABLE_SYMBOLS = [
  ...DEFAULT_SYMBOLS,
  'NVDA', 'AAPL', 'AMZN', 'GOOGL', 'MSFT'
];

const currentYear = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from({ length: Math.max(1, currentYear - 2011 + 1) }, (_, i) => currentYear - i);

export default function PortfolioOptimizer(props) {
  // Plan ID strictly follows props.planId with no override allowed
  const planId = props.planId || 192;
  const currentUserId = props.userId || 1;

  // -------------------------------------------------------------
  // CONFIGURATION STATE (matching orchestrator/optimizer.js defaults)
  // -------------------------------------------------------------
  const [rebalanceExistingPlan, setRebalanceExistingPlan] = useState(false);
  const [rebalanceMaxSwaps, setRebalanceMaxSwaps] = useState(3);
  const [exhaustAllAttempts, setExhaustAllAttempts] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState(50);
  const [iterations, setIterations] = useState(500000);
  const [portfolioSize, setPortfolioSize] = useState(30);

  const [minAllocSize, setMinAllocSize] = useState(4);
  const [maxAllocSize, setMaxAllocSize] = useState(8);
  const [shortAllocReduction, setShortAllocReduction] = useState(0);
  const [allocStep, setAllocStep] = useState(1);
  const [floor, setFloor] = useState(-2);
  const [maxSymbolPct, setMaxSymbolPct] = useState(40);
  const [maxTypePct, setMaxTypePct] = useState(60);
  const [maxAlgosPerLifecycle, setMaxAlgosPerLifecycle] = useState(2);
  const [minBaseHittersPct, setMinBaseHittersPct] = useState(10);
  const [minHomeRunHittersPct, setMinHomeRunHittersPct] = useState(10);
  const [minShortPct, setMinShortPct] = useState(10);

  const [minCagr, setMinCagr] = useState(25.0);
  const [maxDrawdown, setMaxDrawdown] = useState(8);
  const [oosCagrBuffer, setOosCagrBuffer] = useState(50);
  const [oosDdBuffer, setOosDdBuffer] = useState(25);
  const [targetYears, setTargetYears] = useState([2022, 2020, 2018, 2015, 2026]);

  const [acceptableSymbols, setAcceptableSymbols] = useState(DEFAULT_SYMBOLS);

  const allSymbols = Array.from(new Set([...AVAILABLE_SYMBOLS, ...acceptableSymbols]));

  const handleToggleSymbol = (sym) => {
    if (running) return;
    setAcceptableSymbols((prev) =>
      prev.includes(sym) ? prev.filter((s) => s !== sym) : [...prev, sym]
    );
  };

  const handleSelectAllSymbols = () => {
    if (running) return;
    setAcceptableSymbols([...allSymbols]);
  };

  const handleClearAllSymbols = () => {
    if (running) return;
    setAcceptableSymbols([]);
  };
  const [excludeAlgoIdsStr, setExcludeAlgoIdsStr] = useState('');
  const [mustIncludeAlgoIdsStr, setMustIncludeAlgoIdsStr] = useState('');

  const [weightSharpe, setWeightSharpe] = useState(0.20);
  const [weightOos, setWeightOos] = useState(0.20);
  const [weightParamSens, setWeightParamSens] = useState(0.20);
  const [weightCagr, setWeightCagr] = useState(0.20);
  const [weightMaxDd, setWeightMaxDd] = useState(0.20);
  const [fitnessYears, setFitnessYears] = useState(10);

  const [skipBacktest, setSkipBacktest] = useState(false);
  const [skipSave, setSkipSave] = useState(true);
  const [runOfficialBacktest, setRunOfficialBacktest] = useState(true);
  const [useUnrealized, setUseUnrealized] = useState(true);

  // -------------------------------------------------------------
  // PRESETS & SAVED CONFIGURATIONS
  // -------------------------------------------------------------
  const [presets, setPresets] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('preset_balanced');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveConfigName, setSaveConfigName] = useState('');
  const [configSuccessMsg, setConfigSuccessMsg] = useState(null);
  const [configErrorMsg, setConfigErrorMsg] = useState(null);

  // -------------------------------------------------------------
  // JOB RUNNING & EXECUTION STATE
  // -------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('config'); // 'config' | 'results' | 'logs'
  const [configSubTab, setConfigSubTab] = useState('general');
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState({ percent: 0, attempt: 0, maxAttempts: 10, stage: 0, totalStages: 5, message: '' });
  const [logs, setLogs] = useState([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [jobResult, setJobResult] = useState(null);
  const [selectedPlanView, setSelectedPlanView] = useState(null);
  const [allocSortField, setAllocSortField] = useState(null);
  const [allocSortAsc, setAllocSortAsc] = useState(true);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const [logFilter, setLogFilter] = useState('');

  // -------------------------------------------------------------
  // NOTIFICATIONS
  // -------------------------------------------------------------
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted'
  );
  const [notificationAlert, setNotificationAlert] = useState(null);

  // -------------------------------------------------------------
  // DEPLOYMENT MODAL
  // -------------------------------------------------------------
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [planToDeploy, setPlanToDeploy] = useState(null);
  const [deploying, setDeploying] = useState(false);
  const [deployResult, setDeployResult] = useState(null);

  // -------------------------------------------------------------
  // BACKTEST RESULTS DRAWER
  // -------------------------------------------------------------
  const [showBacktestDrawer, setShowBacktestDrawer] = useState(false);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestPerformance, setBacktestPerformance] = useState(null);
  const [backtestChartData, setBacktestChartData] = useState([]);
  const [backtestPlanTitle, setBacktestPlanTitle] = useState('');
  const [backtestError, setBacktestError] = useState(null);

  const logsEndRef = useRef(null);
  const pollTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);

  // Load presets and check for running job on mount
  useEffect(() => {
    fetchConfigs();
    checkForRunningJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, planId]);

  // When tab/window regains focus or visibility, immediately sync status with server
  useEffect(() => {
    const handleSyncOnReturn = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (jobId) {
        pollJobStatus(jobId);
      } else {
        checkForRunningJob();
      }
    };
    document.addEventListener('visibilitychange', handleSyncOnReturn);
    window.addEventListener('focus', handleSyncOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', handleSyncOnReturn);
      window.removeEventListener('focus', handleSyncOnReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, running, planId]);

  // Auto scroll logs
  useEffect(() => {
    if (autoScrollLogs && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScrollLogs]);

  // Elapsed timer when running
  useEffect(() => {
    if (running) {
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    }
    return () => {
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, [running]);

  // Polling for job updates
  useEffect(() => {
    if (running && jobId) {
      pollTimerRef.current = setInterval(() => {
        pollJobStatus(jobId);
      }, 1500);
    } else {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, jobId]);

  // -------------------------------------------------------------
  // NOTIFICATION HELPERS
  // -------------------------------------------------------------
  const requestNotificationPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        setNotificationsEnabled(permission === 'granted');
        if (permission === 'granted') {
          new Notification('Portfolio Optimizer', {
            body: 'Desktop notifications enabled for optimizer completions!'
          });
        }
      } catch (e) {
        console.warn('Notification permission error:', e);
      }
    }
  };

  const getPlanCagr = (plan) => {
    if (!plan) return null;
    const btResults = plan.backtestResults || plan.inSampleResponse || plan.inSampleMetrics;
    const periodAvgYearly = btResults?.periodSummaries?.yearly?.avgResult ?? plan.periodSummaries?.yearly?.avgResult;
    if (periodAvgYearly != null && !isNaN(Number(periodAvgYearly))) return Number(periodAvgYearly);

    if (plan.cagr != null && !isNaN(Number(plan.cagr))) return Number(plan.cagr);
    if (plan.inSampleMetrics?.cagr != null && !isNaN(Number(plan.inSampleMetrics.cagr))) return Number(plan.inSampleMetrics.cagr);
    if (plan.summary?.meanCagr != null && !isNaN(Number(plan.summary.meanCagr))) return Number(plan.summary.meanCagr);
    if (plan.meanCagr != null && !isNaN(Number(plan.meanCagr))) return Number(plan.meanCagr);
    return null;
  };

  const getPlanMaxDd = (plan) => {
    if (!plan) return null;
    const btResults = plan.backtestResults || plan.inSampleResponse || plan.inSampleMetrics;
    const periodDailyMaxDd = btResults?.periodSummaries?.daily?.maxDrawdown ?? plan.periodSummaries?.daily?.maxDrawdown;
    if (periodDailyMaxDd != null && !isNaN(Number(periodDailyMaxDd))) return Math.abs(Number(periodDailyMaxDd));

    if (plan.maxDrawdown != null && !isNaN(Number(plan.maxDrawdown))) return Math.abs(Number(plan.maxDrawdown));
    if (plan.drawdownMagnitude != null && !isNaN(Number(plan.drawdownMagnitude))) return Math.abs(Number(plan.drawdownMagnitude));
    if (plan.inSampleMetrics?.maxDrawdown != null && !isNaN(Number(plan.inSampleMetrics.maxDrawdown))) return Math.abs(Number(plan.inSampleMetrics.maxDrawdown));
    if (plan.inSampleMetrics?.drawdownMagnitude != null && !isNaN(Number(plan.inSampleMetrics.drawdownMagnitude))) return Math.abs(Number(plan.inSampleMetrics.drawdownMagnitude));
    if (plan.summary?.meanMaxDD != null && !isNaN(Number(plan.summary.meanMaxDD))) return Math.abs(Number(plan.summary.meanMaxDD));
    if (plan.meanMaxDD != null && !isNaN(Number(plan.meanMaxDD))) return Math.abs(Number(plan.meanMaxDD));
    return null;
  };

  const getPlanSharpe = (plan) => {
    if (!plan) return null;
    if (plan.sharpeRatio != null && !isNaN(Number(plan.sharpeRatio))) return Number(plan.sharpeRatio);
    if (plan.sharpe != null && !isNaN(Number(plan.sharpe))) return Number(plan.sharpe);
    if (plan.inSampleMetrics?.sharpe != null && !isNaN(Number(plan.inSampleMetrics.sharpe))) return Number(plan.inSampleMetrics.sharpe);
    if (plan.summary?.meanSharpe != null && !isNaN(Number(plan.summary.meanSharpe))) return Number(plan.summary.meanSharpe);
    if (plan.meanSharpe != null && !isNaN(Number(plan.meanSharpe))) return Number(plan.meanSharpe);
    return null;
  };

  const getPlanFitness = (plan) => {
    if (!plan) return null;
    if (plan.fitnessScore != null && !isNaN(Number(plan.fitnessScore))) return Number(plan.fitnessScore);
    if (plan.overallFitness != null && !isNaN(Number(plan.overallFitness))) return Number(plan.overallFitness);
    if (plan.planScore != null && !isNaN(Number(plan.planScore))) return Number(plan.planScore);
    return null;
  };

  const getAlgoAlloc = (plan, algo) => {
    if (!algo) return 6;
    const id = String(algo.idalgos || algo.algoId);
    if (plan?.allocationPayload && plan.allocationPayload[id] !== undefined) {
      return plan.allocationPayload[id];
    }
    if (algo.allocation != null) {
      return algo.allocation;
    }
    if (plan?.allocationSize != null) {
      const isShort =
        (algo.name && algo.name.toLowerCase().includes('short')) ||
        (algo.display_name && algo.display_name.toLowerCase().includes('short')) ||
        (algo.direction && String(algo.direction).toLowerCase() === 'short');
      return isShort ? Math.max(0, plan.allocationSize - (shortAllocReduction || 0)) : plan.allocationSize;
    }
    return 6;
  };

  const getPlanMaxDdDate = (plan) => {
    if (!plan) return null;
    if (plan.maxDrawdownDate) return plan.maxDrawdownDate;
    if (plan.inSampleMetrics?.maxDrawdownDate) return plan.inSampleMetrics.maxDrawdownDate;
    if (plan.oosMetrics?.maxDrawdownDate) return plan.oosMetrics.maxDrawdownDate;
    if (plan.maxDrawdownMonth) return plan.maxDrawdownMonth;
    if (plan.inSampleMetrics?.maxDrawdownMonth) return plan.inSampleMetrics.maxDrawdownMonth;
    if (plan.summary?.maxDrawdownDate) return plan.summary.maxDrawdownDate;
    return null;
  };

  const getAlgoTypeBreakdown = (portfolio) => {
    if (!portfolio || !Array.isArray(portfolio) || portfolio.length === 0) return [];
    const typeMap = {};
    let totalAlloc = 0;

    portfolio.forEach((algo) => {
      const type = algo.algo_type || algo.algoType || 'Unassigned';
      const alloc = Number(getAlgoAlloc(selectedPlanView, algo) || 0);
      totalAlloc += alloc;
      if (!typeMap[type]) {
        typeMap[type] = { type, count: 0, totalAlloc: 0, longCount: 0, shortCount: 0 };
      }
      typeMap[type].count += 1;
      typeMap[type].totalAlloc += alloc;
      const isShort =
        (algo.name && algo.name.toLowerCase().includes('short')) ||
        (algo.display_name && algo.display_name.toLowerCase().includes('short')) ||
        (algo.direction && String(algo.direction).toLowerCase() === 'short');
      if (isShort) typeMap[type].shortCount += 1;
      else typeMap[type].longCount += 1;
    });

    return Object.values(typeMap)
      .map((item) => ({
        ...item,
        pctOfPortfolio: totalAlloc > 0 ? (item.totalAlloc / totalAlloc) * 100 : (item.count / portfolio.length) * 100
      }))
      .sort((a, b) => b.totalAlloc - a.totalAlloc);
  };

  const getSymbolBreakdown = (portfolio) => {
    if (!portfolio || !Array.isArray(portfolio) || portfolio.length === 0) return [];
    const symbolMap = {};
    let totalAlloc = 0;

    portfolio.forEach((algo) => {
      const sym = (algo.trade_symbol || algo.symbol || algo.baseSymbol || 'OTHER').toUpperCase();
      const alloc = Number(getAlgoAlloc(selectedPlanView, algo) || 0);
      totalAlloc += alloc;
      if (!symbolMap[sym]) {
        symbolMap[sym] = { symbol: sym, count: 0, totalAlloc: 0, longCount: 0, shortCount: 0 };
      }
      symbolMap[sym].count += 1;
      symbolMap[sym].totalAlloc += alloc;
      const isShort =
        (algo.name && algo.name.toLowerCase().includes('short')) ||
        (algo.display_name && algo.display_name.toLowerCase().includes('short')) ||
        (algo.direction && String(algo.direction).toLowerCase() === 'short');
      if (isShort) symbolMap[sym].shortCount += 1;
      else symbolMap[sym].longCount += 1;
    });

    return Object.values(symbolMap)
      .map((item) => ({
        ...item,
        pctOfPortfolio: totalAlloc > 0 ? (item.totalAlloc / totalAlloc) * 100 : (item.count / portfolio.length) * 100
      }))
      .sort((a, b) => b.totalAlloc - a.totalAlloc);
  };

  const getShortRatio = (portfolio) => {
    if (!portfolio || !Array.isArray(portfolio) || portfolio.length === 0) return { count: 0, pct: '0.0' };
    const shortCount = portfolio.filter((a) => {
      if (a.short === 1 || a.short === true || a.short === '1') return true;
      if (a.is_short === 1 || a.is_short === true || a.is_short === '1') return true;
      if (a.direction && String(a.direction).toLowerCase() === 'short') return true;
      if (a.name && (a.name.toLowerCase().includes('short') || a.name.toLowerCase().includes('inverse'))) return true;
      if (a.display_name && (a.display_name.toLowerCase().includes('short') || a.display_name.toLowerCase().includes('inverse'))) return true;
      const sym = String(a.trade_symbol || a.symbol || '').trim().toUpperCase();
      return ['SQQQ', 'SPXU', 'SH', 'PSQ', 'DOG', 'SOXS'].includes(sym);
    }).length;
    return {
      count: shortCount,
      pct: ((shortCount / portfolio.length) * 100).toFixed(1)
    };
  };

  const handleAllocSort = (field) => {
    if (allocSortField === field) {
      setAllocSortAsc(!allocSortAsc);
    } else {
      setAllocSortField(field);
      if (['allocation', 'sharpe', 'winRate'].includes(field)) {
        setAllocSortAsc(false);
      } else {
        setAllocSortAsc(true);
      }
    }
  };

  const getSortedPortfolio = (portfolio) => {
    if (!portfolio || !Array.isArray(portfolio)) return [];
    if (!allocSortField) return portfolio;

    return [...portfolio].sort((a, b) => {
      let valA, valB;
      const idA = a.idalgos || a.algoId || 0;
      const idB = b.idalgos || b.algoId || 0;

      switch (allocSortField) {
        case 'id':
          valA = Number(idA);
          valB = Number(idB);
          break;
        case 'symbol':
          valA = (a.symbol || a.trade_symbol || a.baseSymbol || '').toLowerCase();
          valB = (b.symbol || b.trade_symbol || b.baseSymbol || '').toLowerCase();
          break;
        case 'name':
          valA = (a.display_name || a.name || `Algo ${idA}`).toLowerCase();
          valB = (b.display_name || b.name || `Algo ${idB}`).toLowerCase();
          break;
        case 'type':
          valA = (a.algo_type || a.algoType || '').toLowerCase();
          valB = (b.algo_type || b.algoType || '').toLowerCase();
          break;
        case 'direction': {
          const isShortA =
            (a.name && a.name.toLowerCase().includes('short')) ||
            (a.display_name && a.display_name.toLowerCase().includes('short')) ||
            (a.direction && String(a.direction).toLowerCase() === 'short');
          const isShortB =
            (b.name && b.name.toLowerCase().includes('short')) ||
            (b.display_name && b.display_name.toLowerCase().includes('short')) ||
            (b.direction && String(b.direction).toLowerCase() === 'short');
          valA = isShortA ? 'SHORT' : 'LONG';
          valB = isShortB ? 'SHORT' : 'LONG';
          break;
        }
        case 'allocation':
          valA = getAlgoAlloc(selectedPlanView, a);
          valB = getAlgoAlloc(selectedPlanView, b);
          valA = valA != null ? Number(valA) : -Infinity;
          valB = valB != null ? Number(valB) : -Infinity;
          break;
        case 'sharpe':
          valA = a.sharpe != null ? Number(a.sharpe) : -Infinity;
          valB = b.sharpe != null ? Number(b.sharpe) : -Infinity;
          break;
        case 'winRate':
          valA = a.win_rate != null ? Number(a.win_rate) : -Infinity;
          valB = b.win_rate != null ? Number(b.win_rate) : -Infinity;
          break;
        default:
          return 0;
      }

      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === 'string' && typeof valB === 'string') {
        const cmp = valA.localeCompare(valB);
        return allocSortAsc ? cmp : -cmp;
      }

      if (valA < valB) return allocSortAsc ? -1 : 1;
      if (valA > valB) return allocSortAsc ? 1 : -1;
      return 0;
    });
  };

  const renderAllocSortIcon = (field) => {
    if (allocSortField !== field) {
      return <span className="text-muted ms-1" style={{ fontSize: '0.75rem', opacity: 0.35 }}>↕</span>;
    }
    return <span className="ms-1 text-primary fw-bold" style={{ fontSize: '0.75rem' }}>{allocSortAsc ? '▲' : '▼'}</span>;
  };

  const notifyCompletion = (success, bestPlan) => {
    const title = success ? 'Optimization Complete!' : 'Optimization Finished';
    const cagr = getPlanCagr(bestPlan);
    const maxDd = getPlanMaxDd(bestPlan);
    const cagrStr = cagr != null ? `CAGR: ${cagr.toFixed(1)}%` : '';
    const ddStr = maxDd != null ? `Max DD: ${maxDd.toFixed(1)}%` : '';
    const message = success
      ? `Portfolio Optimizer found a qualifying plan! ${cagrStr}${ddStr ? ', ' + ddStr : ''}.`
      : 'Optimizer completed attempts without fully meeting all targets.';

    setNotificationAlert({ success, message });

    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        new Notification(title, {
          body: message,
          icon: '/favicon.ico'
        });
      } catch (e) { }
    }

    // Play subtle audio chime if supported
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.type = success ? 'sine' : 'triangle';
      osc.frequency.setValueAtTime(success ? 587.33 : 329.63, audioCtx.currentTime); // D5 or E4
      gain.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.6);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.6);
    } catch (e) { }
  };

  // -------------------------------------------------------------
  // PRESET MANAGEMENT
  // -------------------------------------------------------------
  const fetchConfigs = async () => {
    try {
      const res = await fetch(`/api/optimizerConfig?userId=${currentUserId}`);
      if (res.ok) {
        const data = await res.json();
        if (data.configs) {
          setPresets(data.configs);
          if (selectedConfigId === 'preset_balanced') {
            const balancedPreset = data.configs.find((p) => p.id === 'preset_balanced');
            if (balancedPreset && balancedPreset.config) {
              applyConfig(balancedPreset.config);
            }
          }
        }
      }
    } catch (e) {
      console.warn('Could not load presets:', e);
    }
  };

  const applyConfig = (cfg) => {
    if (!cfg) return;
    // planId is strictly locked to props.planId
    if (cfg.rebalanceExistingPlan != null) setRebalanceExistingPlan(Boolean(cfg.rebalanceExistingPlan));
    if (cfg.rebalanceMaxSwaps != null) setRebalanceMaxSwaps(Number(cfg.rebalanceMaxSwaps));
    if (cfg.exhaustAllAttempts != null) setExhaustAllAttempts(Boolean(cfg.exhaustAllAttempts));
    if (cfg.maxAttempts != null) setMaxAttempts(Number(cfg.maxAttempts));
    if (cfg.iterations != null) setIterations(Number(cfg.iterations));
    if (cfg.portfolioSize != null) setPortfolioSize(Number(cfg.portfolioSize));

    if (cfg.minAllocSize != null) setMinAllocSize(Number(cfg.minAllocSize));
    if (cfg.maxAllocSize != null) setMaxAllocSize(Number(cfg.maxAllocSize));
    if (cfg.shortAllocReduction != null) setShortAllocReduction(Number(cfg.shortAllocReduction));
    if (cfg.allocStep != null) setAllocStep(Number(cfg.allocStep));
    if (cfg.floor != null) setFloor(Number(cfg.floor));
    if (cfg.maxSymbolPct != null) setMaxSymbolPct(Number(cfg.maxSymbolPct));
    if (cfg.maxTypePct != null) setMaxTypePct(Number(cfg.maxTypePct));
    if (cfg.maxAlgosPerLifecycle != null) setMaxAlgosPerLifecycle(Number(cfg.maxAlgosPerLifecycle));
    if (cfg.minBaseHittersPct != null) {
      const val = Number(cfg.minBaseHittersPct);
      setMinBaseHittersPct(val > 0 && val <= 1 ? Math.round(val * 100) : val);
    }
    if (cfg.minHomeRunHittersPct != null) {
      const val = Number(cfg.minHomeRunHittersPct);
      setMinHomeRunHittersPct(val > 0 && val <= 1 ? Math.round(val * 100) : val);
    }
    if (cfg.minShortPct != null) {
      const val = Number(cfg.minShortPct);
      setMinShortPct(val > 0 && val <= 1 ? Math.round(val * 100) : val);
    } else {
      setMinShortPct(10);
    }

    if (cfg.minCagr != null) setMinCagr(Number(cfg.minCagr));
    if (cfg.maxDrawdown != null) setMaxDrawdown(Number(cfg.maxDrawdown));
    if (cfg.oosCagrBuffer != null) setOosCagrBuffer(Number(cfg.oosCagrBuffer));
    if (cfg.oosDdBuffer != null) setOosDdBuffer(Number(cfg.oosDdBuffer));
    if (cfg.targetYears && Array.isArray(cfg.targetYears)) setTargetYears(cfg.targetYears);

    if (cfg.acceptableSymbols) {
      if (Array.isArray(cfg.acceptableSymbols)) {
        setAcceptableSymbols(cfg.acceptableSymbols.map((s) => String(s).trim().toUpperCase()).filter(Boolean));
      } else if (typeof cfg.acceptableSymbols === 'string') {
        setAcceptableSymbols(cfg.acceptableSymbols.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean));
      }
    } else {
      setAcceptableSymbols(DEFAULT_SYMBOLS);
    }
    if (cfg.excludeAlgoIds) {
      setExcludeAlgoIdsStr(Array.isArray(cfg.excludeAlgoIds) ? cfg.excludeAlgoIds.join(', ') : cfg.excludeAlgoIds);
    }
    if (cfg.mustIncludeAlgoIds) {
      setMustIncludeAlgoIdsStr(Array.isArray(cfg.mustIncludeAlgoIds) ? cfg.mustIncludeAlgoIds.join(', ') : cfg.mustIncludeAlgoIds);
    }

    if (cfg.fitnessWeights) {
      if (cfg.fitnessWeights.sharpe != null) setWeightSharpe(cfg.fitnessWeights.sharpe);
      if (cfg.fitnessWeights.oos != null) setWeightOos(cfg.fitnessWeights.oos);
      if (cfg.fitnessWeights.parameterSensitivity != null) setWeightParamSens(cfg.fitnessWeights.parameterSensitivity);
      if (cfg.fitnessWeights.cagr != null) setWeightCagr(cfg.fitnessWeights.cagr);
      if (cfg.fitnessWeights.maxDrawdown != null) setWeightMaxDd(cfg.fitnessWeights.maxDrawdown);
    }

    if (cfg.skipBacktest != null) setSkipBacktest(Boolean(cfg.skipBacktest));
    if (cfg.skipSave != null) setSkipSave(Boolean(cfg.skipSave));
    if (cfg.runOfficialBacktest != null) setRunOfficialBacktest(Boolean(cfg.runOfficialBacktest));
    if (cfg.useUnrealized != null) setUseUnrealized(Boolean(cfg.useUnrealized));
    if (cfg.fitnessYears != null) setFitnessYears(Number(cfg.fitnessYears));
    else if (cfg.years != null) setFitnessYears(Number(cfg.years));
  };

  const handleSelectPreset = (e) => {
    const selectedId = e.target.value;
    setSelectedConfigId(selectedId);
    const found = presets.find((p) => p.id === selectedId);
    if (found && found.config) {
      applyConfig(found.config);
    }
  };

  const getCurrentConfigPayload = () => {
    const parseList = (str) =>
      str
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

    return {
      planId: Number(planId),
      rebalanceExistingPlan: Boolean(rebalanceExistingPlan),
      rebalanceMaxSwaps: Number(rebalanceMaxSwaps),
      exhaustAllAttempts: Boolean(exhaustAllAttempts),
      maxAttempts: Number(maxAttempts),
      iterations: Number(iterations),
      portfolioSize: Number(portfolioSize),
      portfolioSizeSpecified: true,
      size: Number(portfolioSize),
      minAllocSize: Number(minAllocSize),
      maxAllocSize: Number(maxAllocSize),
      shortAllocReduction: Number(shortAllocReduction),
      allocStep: Number(allocStep),
      floor: Number(floor),
      maxSymbolPct: Number(maxSymbolPct),
      maxTypePct: Number(maxTypePct),
      maxAlgosPerLifecycle: Number(maxAlgosPerLifecycle),
      minBaseHittersPct: Number(minBaseHittersPct) > 1 ? Number(minBaseHittersPct) / 100 : Number(minBaseHittersPct),
      minHomeRunHittersPct: Number(minHomeRunHittersPct) > 1 ? Number(minHomeRunHittersPct) / 100 : Number(minHomeRunHittersPct),
      minShortPct: Number(minShortPct),
      minCagr: Number(minCagr),
      maxDrawdown: Number(maxDrawdown),
      oosCagrBuffer: Number(oosCagrBuffer),
      oosDdBuffer: Number(oosDdBuffer),
      targetYears,
      acceptableSymbols: acceptableSymbols.map((s) => s.toUpperCase()),
      excludeAlgoIds: parseList(excludeAlgoIdsStr).map(Number).filter(n => !isNaN(n)),
      mustIncludeAlgoIds: parseList(mustIncludeAlgoIdsStr).map(Number).filter(n => !isNaN(n)),
      fitnessWeights: {
        sharpe: Number(weightSharpe),
        oos: Number(weightOos),
        parameterSensitivity: Number(weightParamSens),
        cagr: Number(weightCagr),
        maxDrawdown: Number(weightMaxDd)
      },
      skipBacktest: Boolean(skipBacktest),
      skipSave: Boolean(skipSave),
      runOfficialBacktest: Boolean(runOfficialBacktest),
      useUnrealized: Boolean(useUnrealized),
      fitnessYears: Number(fitnessYears)
    };
  };

  const handleSaveAsNewPreset = async () => {
    if (!saveConfigName.trim()) {
      setConfigErrorMsg('Please enter a name for the configuration.');
      return;
    }

    try {
      const payload = {
        name: saveConfigName.trim(),
        userId: currentUserId,
        config: getCurrentConfigPayload()
      };

      const res = await fetch(`/api/optimizerConfig`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setConfigSuccessMsg(`Configuration '${saveConfigName}' saved successfully!`);
        setSaveModalOpen(false);
        setSaveConfigName('');
        await fetchConfigs();
      } else {
        const err = await res.json();
        setConfigErrorMsg(err.error || 'Failed to save configuration.');
      }
    } catch (e) {
      setConfigErrorMsg(e.message || 'Error saving configuration.');
    }
  };

  const handleOverwriteCurrentConfig = async () => {
    const selected = presets.find((p) => p.id === selectedConfigId);
    if (!selected || selected.isPreset) {
      setConfigErrorMsg('Built-in presets cannot be overwritten. Use "Save As New..." instead.');
      return;
    }

    try {
      const payload = {
        name: selected.name,
        userId: currentUserId,
        config: getCurrentConfigPayload()
      };

      const res = await fetch(`/api/optimizerConfig`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setConfigSuccessMsg(`Configuration '${selected.name}' updated!`);
        await fetchConfigs();
      } else {
        const err = await res.json();
        setConfigErrorMsg(err.error || 'Failed to update configuration.');
      }
    } catch (e) {
      setConfigErrorMsg(e.message || 'Error updating configuration.');
    }
  };

  const handleDeleteConfig = async () => {
    const selected = presets.find((p) => p.id === selectedConfigId);
    if (!selected || selected.isPreset) {
      setConfigErrorMsg('Built-in presets cannot be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the configuration '${selected.name}'?`)) {
      return;
    }

    try {
      const res = await fetch(`/api/optimizerConfig?name=${encodeURIComponent(selected.name)}&userId=${currentUserId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        setConfigSuccessMsg(`Configuration '${selected.name}' deleted.`);
        setSelectedConfigId('preset_balanced');
        applyConfig(presets.find((p) => p.id === 'preset_balanced')?.config);
        await fetchConfigs();
      } else {
        const err = await res.json();
        setConfigErrorMsg(err.error || 'Failed to delete configuration.');
      }
    } catch (e) {
      setConfigErrorMsg(e.message || 'Error deleting configuration.');
    }
  };

  // -------------------------------------------------------------
  // RUNNING & CONTROLLING OPTIMIZER
  // -------------------------------------------------------------
  const checkForRunningJob = async () => {
    try {
      const storedJobId = typeof window !== 'undefined'
        ? localStorage.getItem(`optimizer_running_job_${planId}`)
        : null;

      let jobToResume = null;
      let completedJobToRestore = null;

      // 1. Check server directly for any running job for this planId
      try {
        const res = await fetch(`/api/optimizerRun?planId=${planId}&findRunning=true`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.job && (data.job.status === 'running' || data.job.status === 'cancelling')) {
            jobToResume = data.job;
          }
        }
      } catch (e) {
        console.warn('Error querying server for running job:', e);
      }

      // 2. If no running job found on server query, check storedJobId from localStorage
      if (!jobToResume && storedJobId) {
        try {
          const storedRes = await fetch(`/api/optimizerRun?jobId=${storedJobId}`);
          if (storedRes.ok) {
            const storedData = await storedRes.json();
            const storedJob = storedData.job;
            if (storedJob) {
              if (storedJob.status === 'running' || storedJob.status === 'cancelling') {
                jobToResume = storedJob;
              } else if (storedJob.status === 'completed' || storedJob.status === 'completed_unmet') {
                completedJobToRestore = storedJob;
              } else {
                try {
                  localStorage.removeItem(`optimizer_running_job_${planId}`);
                } catch (e) {}
              }
            }
          }
        } catch (e) {
          console.warn('Error checking stored jobId:', e);
        }
      }

      // 3. Fallback check: see if the server has a recent job for this plan (< 2 hours)
      if (!jobToResume && !completedJobToRestore) {
        try {
          const planRes = await fetch(`/api/optimizerRun?planId=${planId}`);
          if (planRes.ok) {
            const planData = await planRes.json();
            const candidate = planData.job;
            if (candidate) {
              if (candidate.status === 'running' || candidate.status === 'cancelling') {
                jobToResume = candidate;
              } else if (candidate.status === 'completed' || candidate.status === 'completed_unmet') {
                const ageMs = Date.now() - new Date(candidate.startedAt).getTime();
                if (ageMs < 2 * 60 * 60 * 1000) {
                  completedJobToRestore = candidate;
                }
              }
            }
          }
        } catch (e) {
          console.warn('Error checking recent plan jobs:', e);
        }
      }

      // Handle restoring completed job
      if (completedJobToRestore) {
        try {
          localStorage.removeItem(`optimizer_running_job_${planId}`);
        } catch (e) {}
        setJobId(completedJobToRestore.id);
        setRunning(false);
        if (completedJobToRestore.logs && Array.isArray(completedJobToRestore.logs)) setLogs(completedJobToRestore.logs);
        if (completedJobToRestore.progress) setProgress(completedJobToRestore.progress);
        const resolvedResult = completedJobToRestore.result || {
          success: completedJobToRestore.status === 'completed',
          validationPassed: completedJobToRestore.status === 'completed',
          topPlans: completedJobToRestore.topPlans || [],
          bestPlan: completedJobToRestore.bestPlan || (completedJobToRestore.topPlans && completedJobToRestore.topPlans[0]) || null,
          planId: completedJobToRestore.planId
        };
        setJobResult(resolvedResult);
        const planToView = resolvedResult.bestPlan || (resolvedResult.topPlans && resolvedResult.topPlans[0]) || completedJobToRestore.bestPlan || null;
        setSelectedPlanView(planToView);
        return;
      }

      // 4. Resume running job if active
      if (jobToResume) {
        console.log(`[PortfolioOptimizer] Resuming active job ${jobToResume.id} for plan ${planId}`);
        setJobId(jobToResume.id);
        setRunning(true);
        if (jobToResume.progress) setProgress(jobToResume.progress);
        if (jobToResume.logs && Array.isArray(jobToResume.logs)) setLogs(jobToResume.logs);
        if (jobToResume.topPlans && jobToResume.topPlans.length > 0) {
          const liveResult = {
            isLive: true,
            success: false,
            validationPassed: false,
            topPlans: jobToResume.topPlans,
            bestPlan: jobToResume.bestPlan || jobToResume.topPlans[0],
            planId: jobToResume.planId || planId,
            attemptsRun: jobToResume.progress?.attempt || 1,
            rebalanceSummary: jobToResume.result?.rebalanceSummary || null
          };
          setJobResult(liveResult);
          setSelectedPlanView(jobToResume.bestPlan || jobToResume.topPlans[0]);
        }
        const elapsed = jobToResume.elapsedMs != null
          ? Math.floor(jobToResume.elapsedMs / 1000)
          : Math.max(0, Math.floor((Date.now() - new Date(jobToResume.startedAt).getTime()) / 1000));
        setElapsedSeconds(elapsed);
        try {
          localStorage.setItem(`optimizer_running_job_${planId}`, jobToResume.id);
        } catch (e) {}
        pollJobStatus(jobToResume.id);
      }
    } catch (err) {
      console.warn('Error in checkForRunningJob:', err);
    }
  };

  const startOptimizer = async () => {
    setRunning(true);
    setElapsedSeconds(0);
    setJobResult(null);
    setSelectedPlanView(null);
    setLogs([]);
    setNotificationAlert(null);
    setProgress({ percent: 2, attempt: 0, maxAttempts, stage: 0, totalStages: targetYears.length, message: 'Submitting job to API...' });

    // Request notification permission if not yet requested
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      requestNotificationPermission();
    }

    try {
      const config = getCurrentConfigPayload();
      const res = await fetch('/api/optimizerRun', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.jobId) {
        setJobId(data.jobId);
        try {
          localStorage.setItem(`optimizer_running_job_${planId}`, data.jobId);
        } catch (e) {}
        setActiveTab('logs');
        pollJobStatus(data.jobId);
      } else {
        throw new Error('No jobId returned from API');
      }
    } catch (err) {
      console.error('Failed to start optimizer:', err);
      setRunning(false);
      setProgress((prev) => ({ ...prev, percent: 0, message: `Error: ${err.message}` }));
      setNotificationAlert({ success: false, message: `Failed to start optimizer: ${err.message}` });
    }
  };

  const cancelOptimizer = async () => {
    if (!jobId) return;
    try {
      localStorage.removeItem(`optimizer_running_job_${planId}`);
    } catch (e) {}
    try {
      await fetch(`/api/optimizerRun?action=cancel&jobId=${jobId}`, { method: 'POST' });
      setProgress((prev) => ({ ...prev, message: 'Cancelling run...' }));
    } catch (e) {
      console.error('Cancel request failed:', e);
    }
  };

  const pollJobStatus = async (id) => {
    try {
      const res = await fetch(`/api/optimizerRun?jobId=${id}`);
      if (!res.ok) return;

      const data = await res.json();
      const job = data.job;
      if (!job) return;

      if (job.progress) {
        setProgress(job.progress);
      }
      if (job.logs && Array.isArray(job.logs)) {
        setLogs(job.logs);
      }
      if (job.elapsedMs != null) {
        setElapsedSeconds(Math.floor(job.elapsedMs / 1000));
      }

      // Live intermediate results streaming while optimizer is running
      if (job.status === 'running' || job.status === 'cancelling') {
        if (job.topPlans && job.topPlans.length > 0) {
          const liveResult = {
            isLive: true,
            success: false,
            validationPassed: false,
            topPlans: job.topPlans,
            bestPlan: job.bestPlan || job.topPlans[0],
            planId: job.planId || planId,
            attemptsRun: job.progress?.attempt || 1,
            rebalanceSummary: job.result?.rebalanceSummary || null
          };
          setJobResult(liveResult);
          setSelectedPlanView((prev) => {
            if (!prev) return job.bestPlan || job.topPlans[0];
            const matching = job.topPlans.find(p => p.signature === prev.signature);
            return matching || job.bestPlan || job.topPlans[0];
          });
        }
      }

      if (job.status === 'completed' || job.status === 'completed_unmet') {
        setRunning(false);
        try {
          localStorage.removeItem(`optimizer_running_job_${planId}`);
        } catch (e) {}
        const resolvedResult = job.result || {
          success: job.status === 'completed',
          validationPassed: job.status === 'completed',
          topPlans: job.topPlans || [],
          bestPlan: job.bestPlan || (job.topPlans && job.topPlans[0]) || null,
          planId: job.planId
        };
        setJobResult(resolvedResult);
        const planToView = resolvedResult.bestPlan || (resolvedResult.topPlans && resolvedResult.topPlans[0]) || job.bestPlan || null;
        setSelectedPlanView(planToView);
        setActiveTab('results');
        notifyCompletion(job.status === 'completed', planToView);
      } else if (job.status === 'failed') {
        setRunning(false);
        try {
          localStorage.removeItem(`optimizer_running_job_${planId}`);
        } catch (e) {}
        setJobResult(null);
        setProgress((prev) => ({ ...prev, message: `Failed: ${job.error || 'Unknown error'}` }));
        setNotificationAlert({ success: false, message: `Optimizer failed: ${job.error || 'Server error'}` });
      } else if (job.status === 'cancelled') {
        setRunning(false);
        try {
          localStorage.removeItem(`optimizer_running_job_${planId}`);
        } catch (e) {}
        setProgress((prev) => ({ ...prev, message: 'Run was cancelled.' }));
        setNotificationAlert({ success: false, message: 'Optimizer run was cancelled.' });
      }
    } catch (e) {
      console.warn('Error polling job status:', e);
    }
  };

  // -------------------------------------------------------------
  // DEPLOYMENT ACTION
  // -------------------------------------------------------------
  const getPlanPayload = (targetPlan) => {
    if (!targetPlan) return {};
    if (targetPlan.allocationPayload && Object.keys(targetPlan.allocationPayload).length > 0) {
      return targetPlan.allocationPayload;
    }
    const payload = {};
    if (targetPlan.portfolio && Array.isArray(targetPlan.portfolio)) {
      targetPlan.portfolio.forEach((algo) => {
        const id = String(algo.idalgos || algo.algoId);
        payload[id] = getAlgoAlloc(targetPlan, algo);
      });
    }
    return payload;
  };

  const handleOpenDeployModal = (plan) => {
    setPlanToDeploy(plan || jobResult?.bestPlan);
    setDeployResult(null);
    setDeployModalOpen(true);
  };

  const handleConfirmDeploy = async () => {
    if (!planToDeploy) return;

    const payload = getPlanPayload(planToDeploy);

    if (!payload || Object.keys(payload).length === 0) {
      setDeployResult({ success: false, error: 'No algorithm allocations available to deploy.' });
      return;
    }

    setDeploying(true);
    setDeployResult(null);

    try {
      const res = await fetch('/api/optimizerRun', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'deploy',
          planId: Number(planId),
          allocations: payload,
          runOfficialBacktest
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setDeployResult({ success: true, message: data.message, officialBacktest: data.officialBacktest });
      } else {
        setDeployResult({ success: false, error: data.error || 'Deployment failed.' });
      }
    } catch (e) {
      setDeployResult({ success: false, error: e.message || 'Network error deploying allocations.' });
    } finally {
      setDeploying(false);
    }
  };

  // -------------------------------------------------------------
  // FULL PLAN BACKTEST ACTION (OOS START 2011-01-01 THRU PRESENT)
  // -------------------------------------------------------------
  const handleRunPlanBacktest = async (targetPlan, planIndex = null) => {
    const planToRun = targetPlan || selectedPlanView || jobResult?.bestPlan;
    if (!planToRun) return;

    const payload = getPlanPayload(planToRun);
    if (!payload || Object.keys(payload).length === 0) {
      alert('No algorithm allocations found for this candidate portfolio.');
      return;
    }

    const title = planIndex != null
      ? `Candidate Plan #${planIndex + 1}`
      : planToRun === jobResult?.bestPlan
      ? 'Top Ranked Best Plan'
      : 'Selected Candidate Plan';

    setBacktestPlanTitle(title);
    setShowBacktestDrawer(true);
    setBacktestLoading(true);
    setBacktestPerformance(null);
    setBacktestChartData([]);
    setBacktestError(null);

    try {
      const oosStartDate = '2011-01-01';
      const today = new Date().toISOString().split('T')[0];
      const targetPlanId = planId || jobResult?.planId || 192;
      const url = `/api/backtestRunComplete?planId=${targetPlanId}&startDate=${oosStartDate}&endDate=${today}&startAmount=100000&activeTradesOnly=0&liveTradesOnly=0&deleteResults=0&allocationStrategyId=4&isOfficial=0`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error(`Backtest failed with status: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      if (data && data.dailyResults) {
        const tempChartData = [];
        data.dailyResults.forEach((day) => {
          tempChartData.push([new Date(day.date), day.total, day.baseline]);
        });
        setBacktestChartData(tempChartData);
      }
      setBacktestPerformance(data);
    } catch (err) {
      console.error('Plan backtest error:', err);
      setBacktestError(err.message || 'Failed to execute backtest');
    } finally {
      setBacktestLoading(false);
    }
  };

  // -------------------------------------------------------------
  // RENDER HELPERS
  // -------------------------------------------------------------
  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins}:${rem < 10 ? '0' : ''}${rem}`;
  };

  const filteredLogs = logFilter
    ? logs.filter((l) => l.toLowerCase().includes(logFilter.toLowerCase()))
    : logs;

  const currentPreset = presets.find((p) => p.id === selectedConfigId);

  const getDeployItems = () => {
    if (!planToDeploy) return [];
    if (planToDeploy.portfolio && planToDeploy.portfolio.length > 0) {
      return planToDeploy.portfolio.map((algo) => {
        const id = String(algo.idalgos || algo.algoId);
        const name = algo.display_name || algo.name || algo.displayName || algo.algo_name || `Algo #${id}`;
        const symbol = algo.symbol || algo.trade_symbol || algo.baseSymbol || '-';
        const allocPct = getAlgoAlloc(planToDeploy, algo);
        const isShort =
          (algo.name && algo.name.toLowerCase().includes('short')) ||
          (algo.display_name && algo.display_name.toLowerCase().includes('short')) ||
          (algo.direction && String(algo.direction).toLowerCase() === 'short');
        return { id, name, symbol, allocPct, isShort };
      });
    }

    if (planToDeploy.allocationPayload && Object.keys(planToDeploy.allocationPayload).length > 0) {
      return Object.entries(planToDeploy.allocationPayload).map(([algoId, pct]) => {
        const algoObj =
          jobResult?.portfolio?.find((a) => String(a.idalgos || a.algoId) === String(algoId)) ||
          jobResult?.bestPlan?.portfolio?.find((a) => String(a.idalgos || a.algoId) === String(algoId));
        const name =
          algoObj?.display_name || algoObj?.name || algoObj?.displayName || algoObj?.algo_name || `Algo #${algoId}`;
        const symbol = algoObj?.symbol || algoObj?.trade_symbol || algoObj?.baseSymbol || '-';
        const isShort =
          (algoObj?.name && algoObj.name.toLowerCase().includes('short')) ||
          (algoObj?.display_name && algoObj.display_name.toLowerCase().includes('short')) ||
          (algoObj?.direction && String(algoObj.direction).toLowerCase() === 'short');
        return { id: algoId, name, symbol, allocPct: pct, isShort };
      });
    }

    return [];
  };

  const deployItems = getDeployItems();
  const totalAllocPct = deployItems.reduce((sum, item) => sum + (Number(item.allocPct) || 0), 0);

  return (
    <div className="container-fluid py-3" style={{ maxWidth: '1400px' }}>
      {/* HEADER SECTION */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 className="mb-0 fw-bold">Portfolio Optimizer</h3>
          <small className="text-muted">
            Multi-stage walk-forward hill-climbing portfolio optimizer with simulated annealing & OOS stress testing.
          </small>
        </div>

        <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
          {notificationsEnabled ? (
            <Badge bg="success" className="py-2 px-3">
              🔔 Desktop Alerts On
            </Badge>
          ) : (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={requestNotificationPermission}
              title="Enable desktop notifications when optimization finishes"
            >
              🔔 Enable Notifications
            </Button>
          )}

          {running ? (
            <Button variant="danger" size="sm" onClick={cancelOptimizer} className="d-flex align-items-center gap-1">
              <CircularProgress size={14} color="inherit" /> Stop Optimizer
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={startOptimizer} className="fw-bold px-3">
              ▶ Run Optimizer
            </Button>
          )}
        </div>
      </div>

      {/* NOTIFICATION ALERT BANNER */}
      {notificationAlert && (
        <Alert
          variant={notificationAlert.success ? 'success' : 'danger'}
          dismissible
          onClose={() => setNotificationAlert(null)}
          className="mb-3 py-2"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          <strong>{notificationAlert.success ? '✓ Complete: ' : 'Error: '}</strong>
          {notificationAlert.message}
        </Alert>
      )}

      {/* SUCCESS / ERROR NOTICES FOR CONFIG PRESETS */}
      {configSuccessMsg && (
        <Alert variant="info" dismissible onClose={() => setConfigSuccessMsg(null)} className="py-2 mb-2">
          {configSuccessMsg}
        </Alert>
      )}
      {configErrorMsg && (
        <Alert variant="danger" dismissible onClose={() => setConfigErrorMsg(null)} className="py-2 mb-2">
          {configErrorMsg}
        </Alert>
      )}

      {/* LIVE PROGRESS BAR & RUNNING STATUS */}
      {(running || progress.percent > 0) && (
        <Card className="mb-3 border-primary shadow-sm">
          <Card.Body className="py-2">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="fw-bold text-primary">
                {running ? '⚙ Optimizer Running...' : progress.percent === 100 ? '✓ Optimization Finished' : 'Execution Status'}
              </span>
              <div className="d-flex align-items-center gap-2">
                {progress.attempt > 0 && (
                  <Badge bg="secondary">
                    Attempt {progress.attempt} / {progress.maxAttempts}
                  </Badge>
                )}
                <Badge bg="info">Elapsed: {formatTime(elapsedSeconds)}</Badge>
              </div>
            </div>

            <ProgressBar
              animated={running}
              variant={progress.percent === 100 ? 'success' : 'primary'}
              now={progress.percent}
              label={`${Math.round(progress.percent)}%`}
              style={{ height: '18px' }}
            />
            <div className="text-muted small mt-1 text-truncate">
              {progress.message || 'Processing algorithm candidates...'}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* TOP NAVIGATION TABS: CONFIGURATION | RESULTS | LIVE LOGS */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="config" title="⚙ Configuration & Strategy Rules">
          {/* PRESETS TOOLBAR */}
          <Card className="mb-3 bg-light">
            <Card.Body className="py-2">
              <Row className="align-items-center g-2">
                <Col md={4} sm={12}>
                  <Form.Group className="d-flex align-items-center gap-2 mb-0">
                    <Form.Label className="mb-0 fw-bold text-nowrap">Preset:</Form.Label>
                    <Form.Select
                      size="sm"
                      value={selectedConfigId}
                      onChange={handleSelectPreset}
                      disabled={running}
                    >
                      <optgroup label="Built-in Presets">
                        {presets.filter((p) => p.isPreset).map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </optgroup>
                      {presets.some((p) => !p.isPreset) && (
                        <optgroup label="Saved User Presets">
                          {presets.filter((p) => !p.isPreset).map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={8} sm={12} className="d-flex flex-wrap gap-2 justify-content-md-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setSaveModalOpen(true)}
                    disabled={running}
                  >
                    Save As New...
                  </Button>

                  {currentPreset && !currentPreset.isPreset && (
                    <>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleOverwriteCurrentConfig}
                        disabled={running}
                      >
                        Update Saved
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDeleteConfig}
                        disabled={running}
                      >
                        Delete
                      </Button>
                    </>
                  )}

                  <Button
                    variant="primary"
                    size="sm"
                    onClick={startOptimizer}
                    disabled={running}
                    className="fw-bold"
                  >
                    ▶ Launch Optimization
                  </Button>
                </Col>
              </Row>

              {currentPreset && (
                <div className="small text-muted mt-1">
                  <em>{currentPreset.description}</em>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* CONFIGURATION SUB-TABS */}
          <Tabs
            activeKey={configSubTab}
            onSelect={(k) => setConfigSubTab(k)}
            className="mb-3"
            variant="pills"
          >
            {/* SUB-TAB 1: PLAN & REBALANCE */}
            <Tab eventKey="general" title="1. Plan & Rebalancing">
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Target Plan</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                          <Badge bg="primary" className="fs-6 py-2 px-3">
                            Plan #{planId}
                          </Badge>
                          <span className="text-muted small">
                            (Locked to selected plan)
                          </span>
                        </div>
                        <Form.Text className="text-muted">
                          Portfolio optimizations and allocations will be tied to this plan.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Portfolio Size (Algos)</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={5}
                          max={50}
                          value={portfolioSize}
                          onChange={(e) => setPortfolioSize(e.target.value)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Number of distinct algorithms in the portfolio (default 30).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Card className="p-3 border-info bg-light">
                        <Form.Check
                          type="switch"
                          id="rebalance-mode-switch"
                          label={
                            <span className="fw-bold">
                              Rebalance Existing Plan (Seed from Current Live Plan Allocations)
                            </span>
                          }
                          checked={rebalanceExistingPlan}
                          onChange={(e) => setRebalanceExistingPlan(e.target.checked)}
                          disabled={running}
                        />
                        <div className="small text-muted mt-1">
                          When checked, loads existing algorithms from <code>/api/algos?planId={planId}</code> as the baseline,
                          retains core winners, and searches for superior replacements for underperforming positions.
                        </div>

                        {rebalanceExistingPlan && (
                          <div className="mt-3 pt-2 border-top">
                            <Row className="align-items-center">
                              <Col md={6}>
                                <Form.Label className="fw-bold text-primary mb-0">
                                  Max Algos to Swap / Replace:
                                </Form.Label>
                              </Col>
                              <Col md={6}>
                                <Form.Control
                                  type="number"
                                  size="sm"
                                  min={1}
                                  max={portfolioSize}
                                  value={rebalanceMaxSwaps}
                                  onChange={(e) => setRebalanceMaxSwaps(e.target.value)}
                                  disabled={running}
                                  style={{ maxWidth: '120px' }}
                                />
                              </Col>
                            </Row>
                            <Form.Text className="text-muted">
                              Limits how many algorithms can be changed during this rebalance (default 3 swaps maintains portfolio continuity).
                            </Form.Text>
                          </div>
                        )}
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Hill-Climbing Iterations</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={100}
                          max={2000000}
                          step={10000}
                          value={iterations}
                          onChange={(e) => setIterations(e.target.value)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Number of candidate swaps evaluated per walk-forward stage (default 500,000).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Execution Attempts</Form.Label>
                        <Form.Control
                          type="number"
                          size="sm"
                          min={1}
                          max={200}
                          value={maxAttempts}
                          onChange={(e) => setMaxAttempts(e.target.value)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Restarts from new random/rebalance seeds if goals are not met (default 50).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="pt-4">
                        <Form.Check
                          type="checkbox"
                          id="exhaust-attempts-check"
                          label="Exhaust All Attempts"
                          checked={exhaustAllAttempts}
                          onChange={(e) => setExhaustAllAttempts(e.target.checked)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Runs all attempts to find optimal fitness rather than stopping at the first pass.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* SUB-TAB 2: ALLOCATIONS & SIZING */}
            <Tab eventKey="allocations" title="2. Allocations & Leverage">
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Minimum Allocation Size (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={1}
                            max={50}
                            value={minAllocSize}
                            onChange={(e) => setMinAllocSize(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Lowest weight for any single algorithm (default 6%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Maximum Allocation Size (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={1}
                            max={50}
                            value={maxAllocSize}
                            onChange={(e) => setMaxAllocSize(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Upper ceiling for any single algorithm (default 8%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-primary">
                          Short Allocation Reduction (%)
                        </Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={0}
                            max={10}
                            value={shortAllocReduction}
                            onChange={(e) => setShortAllocReduction(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Deducts X% from allocations of all short algorithms (default 0%).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Allocation Step Size (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={1}
                            max={5}
                            value={allocStep}
                            onChange={(e) => setAllocStep(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Granularity for allocation increments (default 1%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Monthly Return Floor (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            step="0.1"
                            value={floor}
                            onChange={(e) => setFloor(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Worst allowable historical monthly drawdown floor (default 0.0%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Portfolio % in Single Symbol</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={10}
                            max={100}
                            value={maxSymbolPct}
                            onChange={(e) => setMaxSymbolPct(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Diversification cap across tickers (default 40%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Portfolio % in Strategy Type</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={10}
                            max={100}
                            value={maxTypePct}
                            onChange={(e) => setMaxTypePct(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Prevents strategy over-concentration (default 60%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Algos per Lifecycle</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={1}
                            max={10}
                            value={maxAlgosPerLifecycle}
                            onChange={(e) => setMaxAlgosPerLifecycle(e.target.value)}
                            disabled={running}
                          />
                        </InputGroup>
                        <Form.Text className="text-muted">Max algos sharing symbol, direction (Long/Short), strategy type, entry speed &amp; duration (default 2).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Min Base Hitters (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step="1"
                            value={minBaseHittersPct}
                            onChange={(e) => setMinBaseHittersPct(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Min portfolio % with win rate &gt; 65% (default 10%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Min Home Run Hitters (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step="1"
                            value={minHomeRunHittersPct}
                            onChange={(e) => setMinHomeRunHittersPct(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Min portfolio % with win rate &lt; 55% &amp; PF &gt; 1.8 (default 10%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Min Short Percentage (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={0}
                            max={100}
                            step="1"
                            value={minShortPct}
                            onChange={(e) => setMinShortPct(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Minimum portfolio % allocated to short strategies (default 10%).</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* SUB-TAB 3: GOALS & OOS TOLERANCES */}
            <Tab eventKey="goals" title="3. Goals & OOS Robustness">
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Minimum Portfolio CAGR (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={5}
                            max={100}
                            value={minCagr}
                            onChange={(e) => setMinCagr(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Target Compound Annual Growth Rate hurdle (default 25.0%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Maximum Drawdown (%)</Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={3}
                            max={50}
                            value={maxDrawdown}
                            onChange={(e) => setMaxDrawdown(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">Maximum historical drawdown tolerance (default 8%).</Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-primary">
                          Out-of-Sample (OOS) CAGR Buffer (%)
                        </Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={5}
                            max={100}
                            value={oosCagrBuffer}
                            onChange={(e) => setOosCagrBuffer(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Looser buffer for OOS walk-forward CAGR (default 50% buffer allows market variation).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-primary">
                          Out-of-Sample (OOS) Max Drawdown Buffer (%)
                        </Form.Label>
                        <InputGroup size="sm">
                          <Form.Control
                            type="number"
                            min={5}
                            max={100}
                            value={oosDdBuffer}
                            onChange={(e) => setOosDdBuffer(e.target.value)}
                            disabled={running}
                          />
                          <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <Form.Text className="text-muted">
                          Strict drawdown buffer on unseen OOS test years (default 25% max expansion).
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Label className="fw-bold">Walk-Forward Target Validation Years</Form.Label>
                      <div className="d-flex flex-wrap gap-3 p-2 border rounded bg-light">
                        {AVAILABLE_YEARS.map((yr) => (
                          <Form.Check
                            inline
                            key={yr}
                            type="checkbox"
                            id={`yr-${yr}`}
                            label={yr}
                            checked={targetYears.includes(yr)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setTargetYears([...targetYears.filter((y) => y !== yr), yr]);
                              } else {
                                setTargetYears(targetYears.filter((y) => y !== yr));
                              }
                            }}
                            disabled={running}
                          />
                        ))}
                      </div>
                      <Form.Text className="text-muted d-block mt-2">
                        {targetYears.length > 0 ? (
                          <div>
                            <span className="fw-bold text-dark">Execution Order:</span>{' '}
                            <span className="text-primary fw-semibold">
                              {targetYears.map((yr, idx) => `Stage ${idx + 1} (${yr})`).join(' → ')}
                            </span>
                            <div className="small text-muted mt-1">
                              Years are locked and evaluated sequentially in this exact order. Newly checked years are appended to the end of the sequence.
                            </div>
                          </div>
                        ) : (
                          <span className="text-danger fw-bold">No target years selected. At least one year is required.</span>
                        )}
                      </Form.Text>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* SUB-TAB 4: SYMBOLS & EXCLUSIONS */}
            <Tab eventKey="filters" title="4. Symbols & Algo Inclusions">
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <Form.Label className="fw-bold mb-0">Acceptable Base Symbols</Form.Label>
                            <small className="text-muted ms-2">
                              ({acceptableSymbols.length} of {allSymbols.length} selected)
                            </small>
                          </div>
                          <div className="d-flex gap-2">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={handleSelectAllSymbols}
                              disabled={running}
                            >
                              Select All
                            </Button>
                            <span className="text-muted">|</span>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none text-danger"
                              onClick={handleClearAllSymbols}
                              disabled={running}
                            >
                              Clear All
                            </Button>
                          </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 p-2 bg-light rounded border">
                          {allSymbols.map((sym) => {
                            const isChecked = acceptableSymbols.includes(sym);
                            return (
                              <div
                                key={sym}
                                className={`px-2 py-1 rounded border d-flex align-items-center ${
                                  isChecked ? 'bg-white border-primary shadow-sm' : 'bg-transparent text-muted'
                                }`}
                                style={{ cursor: running ? 'default' : 'pointer', userSelect: 'none' }}
                                onClick={() => handleToggleSymbol(sym)}
                              >
                                <Form.Check
                                  type="checkbox"
                                  id={`sym-check-${sym}`}
                                  label={<span className={`ms-1 ${isChecked ? 'fw-bold text-dark' : 'text-muted'}`}>{sym}</span>}
                                  checked={isChecked}
                                  onChange={() => handleToggleSymbol(sym)}
                                  disabled={running}
                                  className="m-0"
                                />
                              </div>
                            );
                          })}
                        </div>
                        {acceptableSymbols.length === 0 && (
                          <Alert variant="warning" className="py-1 px-2 mt-2 mb-0 small">
                            ⚠️ No base symbols selected. The optimizer will have no eligible candidate algorithms.
                          </Alert>
                        )}
                        <Form.Text className="text-muted d-block mt-1">
                          Only algorithms trading checked base tickers will be considered. Default: all checked.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-success">
                          Must-Include Algorithm IDs (Retained / Pinned)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. 104, 215, 388"
                          value={mustIncludeAlgoIdsStr}
                          onChange={(e) => setMustIncludeAlgoIdsStr(e.target.value)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Algorithm IDs that MUST be included and never swapped out.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold text-danger">
                          Excluded Algorithm IDs (Blacklist)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          placeholder="e.g. 12, 45, 99"
                          value={excludeAlgoIdsStr}
                          onChange={(e) => setExcludeAlgoIdsStr(e.target.value)}
                          disabled={running}
                        />
                        <Form.Text className="text-muted">
                          Algorithm IDs that should never be selected into the portfolio.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* SUB-TAB 5: FITNESS WEIGHTS */}
            <Tab eventKey="weights" title="5. Fitness Weights">
              <Card>
                <Card.Body>
                  <p className="text-muted small mb-3">
                    Configure the multi-objective fitness function weighting. Normalization automatically scales the weights.
                  </p>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group className="mb-2">
                        <Form.Label className="fw-bold">
                          Fitness Analysis Lookback Period
                        </Form.Label>
                        <Form.Select
                          size="sm"
                          value={fitnessYears}
                          onChange={(e) => setFitnessYears(Number(e.target.value))}
                          disabled={running}
                          style={{ maxWidth: '320px' }}
                        >
                          <option value={10}>10 Years (Default)</option>
                          <option value={5}>5 Years</option>
                          <option value={3}>3 Years</option>
                          <option value={2}>2 Years</option>
                          <option value={1}>1 Year</option>
                          <option value={0}>0 (Live Trades)</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Determines which lookback stats (from <code>algo_stats</code>) are used for candidate algorithm fitness evaluation.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Sharpe Ratio Weight</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          size="sm"
                          value={weightSharpe}
                          onChange={(e) => setWeightSharpe(e.target.value)}
                          disabled={running}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Out-of-Sample (OOS) Performance</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          size="sm"
                          value={weightOos}
                          onChange={(e) => setWeightOos(e.target.value)}
                          disabled={running}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Parameter Sensitivity</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          size="sm"
                          value={weightParamSens}
                          onChange={(e) => setWeightParamSens(e.target.value)}
                          disabled={running}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">CAGR Weight</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          size="sm"
                          value={weightCagr}
                          onChange={(e) => setWeightCagr(e.target.value)}
                          disabled={running}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Max Drawdown Penalty Weight</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          size="sm"
                          value={weightMaxDd}
                          onChange={(e) => setWeightMaxDd(e.target.value)}
                          disabled={running}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>

            {/* SUB-TAB 6: EXECUTION & BACKTEST OPTIONS */}
            <Tab eventKey="execution" title="6. Execution & Deployment">
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Check
                        type="checkbox"
                        id="skip-backtest-check"
                        label="Skip In-Sample/OOS Validation Backtests (Fast Mode)"
                        checked={skipBacktest}
                        onChange={(e) => setSkipBacktest(e.target.checked)}
                        disabled={running}
                      />
                      <Form.Text className="text-muted d-block">
                        Relies solely on monthly matrix performance estimates, skipping live Azure Functions backtests.
                      </Form.Text>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Tab>

        {/* TOP TAB 2: RESULTS & CANDIDATES */}
        <Tab
          eventKey="results"
          title={
            <span>
              📊 Results{' '}
              {running && jobResult?.isLive && (
                <Badge bg="primary" pill className="ms-1">
                  Live ({jobResult.topPlans?.length || 0})
                </Badge>
              )}
              {!running && jobResult && (
                <Badge bg="success" pill className="ms-1">
                  Ready
                </Badge>
              )}
            </span>
          }
        >
          {!jobResult && (
            <Card className="text-center py-5 text-muted">
              <Card.Body>
                <h5>No Optimization Results Yet</h5>
                <p>Run the optimizer from the configuration tab to generate top portfolio candidates.</p>
                <Button variant="primary" onClick={() => setActiveTab('config')} size="sm">
                  Go to Configuration
                </Button>
              </Card.Body>
            </Card>
          )}

          {jobResult && (
            <div>
              {/* STATUS SUMMARY BANNER */}
              {jobResult.isLive ? (
                <Alert variant="info" className="mb-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1 fw-bold d-flex align-items-center gap-2">
                        <Spinner animation="grow" size="sm" variant="primary" />
                        Live Optimization in Progress
                      </h5>
                      <div className="small">
                        Found <strong>{jobResult.topPlans?.length || 0}</strong> top candidate portfolio(s) so far (Attempt {progress?.attempt || 1}/{progress?.maxAttempts || 50}, Stage {progress?.stage || 0}/{progress?.totalStages || 5}).
                        Results update dynamically as higher-fitness portfolios are discovered.
                      </div>
                    </div>
                    <div className="mt-2 mt-md-0 d-flex gap-2 align-items-center">
                      <Badge bg="primary" className="p-2">
                        Elapsed: {formatTime(elapsedSeconds)}
                      </Badge>
                    </div>
                  </div>
                </Alert>
              ) : (
                <Alert variant={jobResult.validationPassed ? 'success' : 'warning'} className="mb-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1 fw-bold">
                        {jobResult.validationPassed
                          ? '✓ Optimal Portfolio Found & Validated!'
                          : 'Optimization Finished (Targets Partially Met)'}
                      </h5>
                      <div className="small">
                        Attempts run: {jobResult.attemptsRun} | Target Plan: {jobResult.planId} | Mode:{' '}
                        {jobResult.rebalanceSummary?.rebalanceMode ? 'Rebalance' : 'Fresh Search'}
                      </div>
                    </div>

                    <div className="mt-2 mt-md-0 d-flex gap-2">
                      <Button
                        variant="info"
                        onClick={() => handleRunPlanBacktest(selectedPlanView || jobResult.bestPlan)}
                        className="fw-bold text-white"
                        title="Run full backtest from OOS start (2011-01-01) through present"
                      >
                        📈 Backtest OOS (2011-Present)
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleOpenDeployModal(selectedPlanView || jobResult.bestPlan)}
                        className="fw-bold"
                      >
                        🚀 Deploy to Plan {jobResult.planId}
                      </Button>
                    </div>
                  </div>
                </Alert>
              )}

              {/* REBALANCE SUMMARY CARD (IF REBALANCING WAS PERFORMED) */}
              {jobResult.rebalanceSummary && jobResult.rebalanceSummary.rebalanceMode && (
                <Card className="mb-3 border-info shadow-sm">
                  <Card.Header className="bg-info text-white fw-bold">
                    🔄 Rebalance Plan Summary (Swapped {jobResult.rebalanceSummary.replacedCount} of max {jobResult.rebalanceSummary.maxAllowedSwaps} algos)
                  </Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="fw-bold text-success mb-1">
                          ✓ Retained Core Algorithms ({jobResult.rebalanceSummary.retainedCount})
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {jobResult.rebalanceSummary.retainedAlgos?.map((algo) => (
                            <Badge key={algo.idalgos || algo.algoId} bg="success">
                              #{algo.idalgos || algo.algoId} {algo.symbol} {algo.display_name || algo.name}
                            </Badge>
                          ))}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="fw-bold text-danger mb-1">
                          ✕ Removed / Replaced Algorithms ({jobResult.rebalanceSummary.removedAlgos?.length || 0})
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {jobResult.rebalanceSummary.removedAlgos?.map((algo) => (
                            <Badge key={algo.idalgos || algo.algoId} bg="danger">
                              #{algo.idalgos || algo.algoId} {algo.symbol} {algo.display_name || algo.name}
                            </Badge>
                          ))}
                          {(!jobResult.rebalanceSummary.removedAlgos || jobResult.rebalanceSummary.removedAlgos.length === 0) && (
                            <span className="text-muted small">None removed</span>
                          )}
                        </div>
                      </Col>

                      <Col md={4}>
                        <div className="fw-bold text-primary mb-1">
                          + Newly Added Algorithms ({jobResult.rebalanceSummary.replacedAlgos?.length || 0})
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {jobResult.rebalanceSummary.replacedAlgos?.map((algo) => (
                            <Badge key={algo.idalgos || algo.algoId} bg="primary">
                              #{algo.idalgos || algo.algoId} {algo.symbol} {algo.display_name || algo.name}
                            </Badge>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* KPI METRIC CARDS FOR SELECTED / BEST PLAN */}
              {selectedPlanView && (
                <Row className="g-3 mb-3">
                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm">
                      <small className="text-muted">CAGR</small>
                      <h4 className="fw-bold text-success mb-0">
                        {getPlanCagr(selectedPlanView) != null ? `${getPlanCagr(selectedPlanView).toFixed(2)}%` : 'N/A'}
                      </h4>
                    </Card>
                  </Col>

                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm h-100">
                      <small className="text-muted">Max Drawdown</small>
                      <h4 className="fw-bold text-danger mb-0">
                        {getPlanMaxDd(selectedPlanView) != null
                          ? `${getPlanMaxDd(selectedPlanView).toFixed(2)}%`
                          : 'N/A'}
                      </h4>
                      {getPlanMaxDdDate(selectedPlanView) && (
                        <div className="text-muted mt-1 small" style={{ fontSize: '0.72rem' }}>
                          📅 {getPlanMaxDdDate(selectedPlanView)}
                        </div>
                      )}
                    </Card>
                  </Col>

                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm">
                      <small className="text-muted">Sharpe Ratio</small>
                      <h4 className="fw-bold text-primary mb-0">
                        {getPlanSharpe(selectedPlanView) != null ? getPlanSharpe(selectedPlanView).toFixed(2) : 'N/A'}
                      </h4>
                    </Card>
                  </Col>

                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm">
                      <small className="text-muted">Fitness Score</small>
                      <h4 className="fw-bold text-dark mb-0">
                        {getPlanFitness(selectedPlanView) != null ? getPlanFitness(selectedPlanView).toFixed(3) : 'N/A'}
                      </h4>
                    </Card>
                  </Col>

                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm">
                      <small className="text-muted">Total Algos (Shorts)</small>
                      <h4 className="fw-bold text-secondary mb-0">
                        {selectedPlanView.portfolio?.length || selectedPlanView.portfolioIndices?.length || 0}
                        {selectedPlanView.portfolio && (
                          <span className="small text-danger ms-1" style={{ fontSize: '0.78rem' }}>
                            ({getShortRatio(selectedPlanView.portfolio).count} short • {getShortRatio(selectedPlanView.portfolio).pct}%)
                          </span>
                        )}
                      </h4>
                    </Card>
                  </Col>

                  <Col md={2} sm={4} xs={6}>
                    <Card className="text-center p-2 bg-light shadow-sm">
                      <small className="text-muted">Status</small>
                      <h5 className="fw-bold mb-0">
                        {selectedPlanView.validationPassed ? (
                          <Badge bg="success">Validated</Badge>
                        ) : selectedPlanView.oosMetrics ? (
                          <Badge bg="info">OOS Tested</Badge>
                        ) : (
                          <Badge bg="secondary">In-Sample</Badge>
                        )}
                      </h5>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* TOP PLANS LEADERBOARD */}
              {jobResult.topPlans && jobResult.topPlans.length > 0 && (
                <Card className="mb-3">
                  <Card.Header className="fw-bold">
                    🏆 Top Ranked Candidate Portfolios ({jobResult.topPlans.length})
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0 small align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Rank</th>
                          <th>Fitness</th>
                          <th>CAGR</th>
                          <th>Max DD</th>
                          <th>Sharpe</th>
                          <th>Algos</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {jobResult.topPlans.map((plan, idx) => {
                          const isSelected = selectedPlanView === plan;
                          const planCagr = getPlanCagr(plan);
                          const planMaxDd = getPlanMaxDd(plan);
                          const planSharpe = getPlanSharpe(plan);
                          const planFit = getPlanFitness(plan);
                          return (
                            <tr key={idx} className={isSelected ? 'table-primary fw-bold' : ''}>
                              <td>
                                {idx === 0 ? <Badge bg="warning" text="dark">#1 Best</Badge> : `#${idx + 1}`}
                              </td>
                              <td>{planFit != null ? planFit.toFixed(3) : '-'}</td>
                              <td className="text-success">{planCagr != null ? `${planCagr.toFixed(2)}%` : '-'}</td>
                              <td className="text-danger">
                                <div>{planMaxDd != null ? `${planMaxDd.toFixed(2)}%` : '-'}</div>
                                {getPlanMaxDdDate(plan) && (
                                  <div className="text-muted small" style={{ fontSize: '0.7rem' }}>
                                    {getPlanMaxDdDate(plan)}
                                  </div>
                                )}
                              </td>
                              <td>{planSharpe != null ? planSharpe.toFixed(2) : '-'}</td>
                              <td>{plan.portfolio?.length || plan.portfolioIndices?.length || '-'}</td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    variant={isSelected ? 'secondary' : 'outline-primary'}
                                    size="sm"
                                    onClick={() => setSelectedPlanView(plan)}
                                  >
                                    {isSelected ? 'Viewing' : 'Inspect'}
                                  </Button>
                                  <Button
                                    variant="outline-info"
                                    size="sm"
                                    onClick={() => handleRunPlanBacktest(plan, idx)}
                                    title="Run full backtest from OOS (2011-01-01) to present"
                                  >
                                    📈 Backtest
                                  </Button>
                                  <Button
                                    variant="outline-success"
                                    size="sm"
                                    onClick={() => handleOpenDeployModal(plan)}
                                  >
                                    Deploy
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* PORTFOLIO COMPOSITION & DIVERSIFICATION BREAKDOWN */}
              {selectedPlanView && selectedPlanView.portfolio && (
                <Row className="g-3 mb-3">
                  {/* ALGO TYPES BREAKDOWN */}
                  <Col md={6}>
                    <Card className="h-100 shadow-sm">
                      <Card.Header className="py-2 bg-light fw-bold d-flex justify-content-between align-items-center">
                        <span>🏷️ Algo Types Breakdown</span>
                        <Badge bg="secondary">
                          {getAlgoTypeBreakdown(selectedPlanView.portfolio).length} Types
                        </Badge>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <div className="d-flex flex-column gap-2">
                          {getAlgoTypeBreakdown(selectedPlanView.portfolio).map((t, idx) => (
                            <div key={idx}>
                              <div className="d-flex justify-content-between align-items-center small mb-1">
                                <span className="fw-bold">{t.type}</span>
                                <span>
                                  <span className="text-primary fw-bold">{t.pctOfPortfolio.toFixed(1)}%</span>
                                  <span className="text-muted ms-1">({t.totalAlloc}% alloc • {t.count} {t.count === 1 ? 'algo' : 'algos'})</span>
                                </span>
                              </div>
                              <ProgressBar
                                now={t.pctOfPortfolio}
                                variant={idx % 3 === 0 ? 'primary' : idx % 3 === 1 ? 'info' : 'dark'}
                                style={{ height: '6px' }}
                              />
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* TRADE SYMBOLS BREAKDOWN */}
                  <Col md={6}>
                    <Card className="h-100 shadow-sm">
                      <Card.Header className="py-2 bg-light fw-bold d-flex justify-content-between align-items-center">
                        <span>🎯 Trade Symbols Breakdown</span>
                        <Badge bg="secondary">
                          {getSymbolBreakdown(selectedPlanView.portfolio).length} Symbols
                        </Badge>
                      </Card.Header>
                      <Card.Body className="py-2">
                        <div className="d-flex flex-column gap-2">
                          {getSymbolBreakdown(selectedPlanView.portfolio).map((s, idx) => (
                            <div key={idx}>
                              <div className="d-flex justify-content-between align-items-center small mb-1">
                                <div>
                                  <Badge bg="dark" className="me-1">{s.symbol}</Badge>
                                  {s.shortCount > 0 && (
                                    <span className="badge bg-danger text-white py-0 px-1 me-1" style={{ fontSize: '0.65rem' }}>
                                      {s.shortCount} SHORT
                                    </span>
                                  )}
                                  {s.longCount > 0 && (
                                    <span className="badge bg-info text-dark py-0 px-1" style={{ fontSize: '0.65rem' }}>
                                      {s.longCount} LONG
                                    </span>
                                  )}
                                </div>
                                <span>
                                  <span className="text-success fw-bold">{s.pctOfPortfolio.toFixed(1)}%</span>
                                  <span className="text-muted ms-1">({s.totalAlloc}% alloc • {s.count} {s.count === 1 ? 'algo' : 'algos'})</span>
                                </span>
                              </div>
                              <ProgressBar
                                now={s.pctOfPortfolio}
                                variant={s.pctOfPortfolio > 25 ? 'warning' : 'success'}
                                style={{ height: '6px' }}
                              />
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* ALLOCATION COMPOSITION TABLE */}
              {selectedPlanView && selectedPlanView.portfolio && (
                <Card className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">
                      📋 Selected Portfolio Allocations ({selectedPlanView.portfolio.length} Algorithms)
                    </span>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() => handleRunPlanBacktest(selectedPlanView)}
                        className="fw-bold"
                        title="Run full backtest from OOS (2011-01-01) to present"
                      >
                        📈 Backtest OOS
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleOpenDeployModal(selectedPlanView)}
                        className="fw-bold"
                      >
                        Deploy This Portfolio
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive striped className="mb-0 small align-middle">
                      <thead className="table-light">
                        <tr>
                          <th
                            onClick={() => handleAllocSort('id')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Algo ID"
                          >
                            Algo ID {renderAllocSortIcon('id')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('symbol')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Symbol"
                          >
                            Symbol {renderAllocSortIcon('symbol')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('name')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Name"
                          >
                            Name {renderAllocSortIcon('name')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('type')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Type"
                          >
                            Type {renderAllocSortIcon('type')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('direction')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Direction"
                          >
                            Direction {renderAllocSortIcon('direction')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('allocation')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Allocation %"
                          >
                            Allocation % {renderAllocSortIcon('allocation')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('sharpe')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Sharpe"
                          >
                            Sharpe {renderAllocSortIcon('sharpe')}
                          </th>
                          <th
                            onClick={() => handleAllocSort('winRate')}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            title="Sort by Win Rate"
                          >
                            Win Rate {renderAllocSortIcon('winRate')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedPortfolio(selectedPlanView.portfolio).map((algo, i) => {
                          const id = algo.idalgos || algo.algoId;
                          const allocPct = getAlgoAlloc(selectedPlanView, algo);
                          const isShort =
                            (algo.name && algo.name.toLowerCase().includes('short')) ||
                            (algo.display_name && algo.display_name.toLowerCase().includes('short')) ||
                            (algo.direction && String(algo.direction).toLowerCase() === 'short');

                          return (
                            <tr key={algo.idalgos || algo.algoId || i}>
                              <td className="fw-bold">#{id}</td>
                              <td>
                                <Badge bg="dark">{algo.symbol || algo.trade_symbol || algo.baseSymbol || '-'}</Badge>
                              </td>
                              <td>{algo.display_name || algo.name || `Algo ${id}`}</td>
                              <td>{algo.algo_type || algo.algoType || '-'}</td>
                              <td>
                                {isShort ? (
                                  <Badge bg="danger">SHORT {shortAllocReduction > 0 ? `(-${shortAllocReduction}%)` : ''}</Badge>
                                ) : (
                                  <Badge bg="info">LONG</Badge>
                                )}
                              </td>
                              <td className="fw-bold text-primary">{allocPct != null ? `${allocPct}%` : '-'}</td>
                              <td>{algo.sharpe != null ? Number(algo.sharpe).toFixed(2) : '-'}</td>
                              <td>
                                {algo.win_rate != null
                                  ? `${(Number(algo.win_rate) > 1 ? Number(algo.win_rate) : Number(algo.win_rate) * 100).toFixed(1)}%`
                                  : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </div>
          )}
        </Tab>

        {/* TOP TAB 3: LIVE LOG TERMINAL */}
        <Tab eventKey="logs" title={`📝 Live Logs (${logs.length})`}>
          <Card className="bg-dark text-light">
            <Card.Header className="d-flex justify-content-between align-items-center py-2 bg-secondary text-white">
              <span className="fw-bold">Terminal Logs</span>
              <div className="d-flex align-items-center gap-3">
                <Form.Control
                  type="text"
                  placeholder="Filter logs..."
                  size="sm"
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  style={{ maxWidth: '180px' }}
                />
                <Form.Check
                  type="checkbox"
                  id="auto-scroll-check"
                  label="Auto-scroll"
                  checked={autoScrollLogs}
                  onChange={(e) => setAutoScrollLogs(e.target.checked)}
                  className="mb-0 small"
                />
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => navigator.clipboard.writeText(logs.join('\n'))}
                >
                  Copy All
                </Button>
              </div>
            </Card.Header>
            <Card.Body
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                height: '500px',
                overflowY: 'auto',
                backgroundColor: '#1e1e1e'
              }}
            >
              {filteredLogs.map((logLine, idx) => (
                <div
                  key={idx}
                  style={{
                    color: logLine.includes('Error') || logLine.includes('failed')
                      ? '#ff6b6b'
                      : logLine.includes('✓') || logLine.includes('PASSED') || logLine.includes('success')
                        ? '#51cf66'
                        : logLine.includes('Stage') || logLine.includes('Candidate')
                          ? '#74c0fc'
                          : '#f8f9fa'
                  }}
                >
                  {logLine}
                </div>
              ))}
              <div ref={logsEndRef} />
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* SAVE PRESET MODAL */}
      <Modal show={saveModalOpen} onHide={() => setSaveModalOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Optimizer Preset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Configuration Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. My Conservative Growth Plan"
              value={saveConfigName}
              onChange={(e) => setSaveConfigName(e.target.value)}
            />
          </Form.Group>
          <p className="small text-muted mb-0">
            This will save all current inputs, weights, symbols, and rebalance settings to your user profile.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSaveModalOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAsNewPreset}>
            Save Configuration
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DEPLOY ALLOCATIONS CONFIRMATION MODAL */}
      <Modal show={deployModalOpen} onHide={() => setDeployModalOpen(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Deploy Allocations to Plan {planId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deployResult ? (
            <Alert variant={deployResult.success ? 'success' : 'danger'}>
              <h5>{deployResult.success ? '✓ Deployment Succeeded!' : 'Deployment Failed'}</h5>
              <p>{deployResult.message || deployResult.error}</p>
              {deployResult.officialBacktest && (
                <div className="mt-2 small">
                  <strong>Official Backtest Launched:</strong>
                  <div>
                    Period: {deployResult.officialBacktest.startDate} to {deployResult.officialBacktest.endDate} (Test ID: {deployResult.officialBacktest.testId || 'queued'})
                  </div>
                </div>
              )}
            </Alert>
          ) : (
            <div>
              <p>
                You are about to deploy these allocations directly into <strong>Plan {planId}</strong>:
              </p>

              {planToDeploy && (
                <div className="border rounded mb-3 overflow-hidden shadow-sm">
                  <div className="bg-light p-2 border-bottom fw-bold small d-flex justify-content-between align-items-center">
                    <span>
                      📋 Portfolio Allocations ({deployItems.length} Algorithms)
                    </span>
                    <Badge bg="primary">
                      Total Allocated: {totalAllocPct}%
                    </Badge>
                  </div>
                  <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="mb-0 small align-middle">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: '70px' }}>ID</th>
                          <th style={{ width: '80px' }}>Symbol</th>
                          <th>Algorithm Name</th>
                          <th style={{ width: '85px' }}>Side</th>
                          <th style={{ width: '95px' }} className="text-end">Allocation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deployItems.map((item, idx) => (
                          <tr key={idx}>
                            <td className="fw-bold text-muted">#{item.id}</td>
                            <td>
                              <Badge bg="dark">{item.symbol}</Badge>
                            </td>
                            <td className="fw-semibold text-truncate" style={{ maxWidth: '260px' }} title={item.name}>
                              {item.name}
                            </td>
                            <td>
                              {item.isShort ? (
                                <Badge bg="danger">SHORT</Badge>
                              ) : (
                                <Badge bg="info">LONG</Badge>
                              )}
                            </td>
                            <td className="text-end fw-bold text-primary">
                              {item.allocPct}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}

              <Form.Check
                type="checkbox"
                id="modal-run-official-bt"
                label={
                  <span className="fw-bold">
                    Run Official Backtest from 1/1/2011 to present (isOfficial=1)
                  </span>
                }
                checked={runOfficialBacktest}
                onChange={(e) => setRunOfficialBacktest(e.target.checked)}
                className="mb-2"
              />
              <small className="text-muted d-block mb-3">
                Updating allocations will replace active allocations for Plan {planId}.
              </small>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeployModalOpen(false)}>
            Close
          </Button>
          {!deployResult?.success && (
            <Button
              variant="success"
              onClick={handleConfirmDeploy}
              disabled={deploying}
              className="fw-bold"
            >
              {deploying ? (
                <>
                  <CircularProgress size={14} color="inherit" className="me-1" /> Deploying...
                </>
              ) : (
                'Confirm & Deploy Allocations'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* FULL BACKTEST RESULTS BOTTOM DRAWER */}
      <Drawer
        anchor="bottom"
        open={showBacktestDrawer}
        onClose={() => setShowBacktestDrawer(false)}
        PaperProps={{
          style: { maxHeight: '92vh', minHeight: '65vh', padding: '24px', overflowY: 'auto' }
        }}
      >
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
          <div>
            <h5 className="mb-1 fw-bold text-primary">
              📊 Full Backtest Results: {backtestPlanTitle}
            </h5>
            <small className="text-muted">
              Evaluation period: <strong>2011-01-01 (Out-Of-Sample Start)</strong> through present date • Plan ID: <strong>{planId || jobResult?.planId}</strong>
            </small>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowBacktestDrawer(false)}>
            ✕ Close
          </Button>
        </div>

        {backtestLoading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" role="status" className="mb-3" />
            <div className="fw-bold">Running full multi-algo backtest from 2011 to present...</div>
            <small className="text-muted">Simulating historical fills and daily equity curves across all candidate algorithms.</small>
          </div>
        )}

        {backtestError && !backtestLoading && (
          <Alert variant="danger" className="my-3">
            <strong>Error running backtest:</strong> {backtestError}
          </Alert>
        )}

        {!backtestLoading && backtestPerformance && (
          <BacktestResults
            isLoading={backtestLoading}
            mobileView={props.mobileView}
            planId={planId || jobResult?.planId}
            backtestResults={backtestPerformance}
            chartData={backtestChartData}
          />
        )}
      </Drawer>
    </div>
  );
}
